import React, { useCallback, useEffect, useState } from "react";
import { useHistory, Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useTranslation } from "react-i18next";
import fp from "lodash/fp";
import InfiniteScroll from "react-infinite-scroll-component";
import { IconShareRequest } from "@mzc-cloudplex/icons";

/* cores */
import convertUtcToLocal from "../../../cores/convertUtcToLocal";
import compareVersions, { VERSION_CM_9337_RECENT_ASSET_SHARED_ME } from "@cores/version";

/* modules */
import { postDashboardQueryList } from "../../../modules/dashboard";

/* components */
import WidgetWrap from "./components/wrap";
import WidgetHead from "./components/head";
import MediaImage from "./components/MediaImage";
import TooltipUserInfo from "../../tooltip/userInfo";

import { IconCaption, IconVideo, IconAudio, IconFile, IconImage, IconZip, IconRefresh } from "@mzc-cloudplex/icons";
import { getApproversList } from "@modules/shareRequest";
import menuService from "@services/menuService";

const RecentAssetSharedMe = ({ name, title, color, icon, removable, onMove, onDelete }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const stateSelector = createSelector(
        (state) => state.project,
        (state) => state.dashboard,
        (state) => state.stage,
        (project, dashboard, stage) => ({
            activeProjectId: fp.get(`id`, project),
            sharedAssetsToMe: fp.get(`sharedAssetsToMe.data`, dashboard),
            pending: fp.get(`sharedAssetsToMe.pending`, dashboard),
            version: fp.get(`version`, stage),
            user: fp.get(`user`, stage),
        }),
    );
    const state = useSelector(stateSelector);
    const user = state.user;
    const [list, setList] = useState([]);
    const [isEmptyList, setIsEmptyList] = useState(false);
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [listProperty, setListProperty] = useState({
        name: `Shared Asset`,
        resource: `shared-asset`,
        columns: [
            `assetId`,
            `assetName`,
            `assetMediaType`,
            `assetType`,
            `assetThumbnailUrl`,
            `assetStatus`,
            `projectId`,
            `projectName`,
            `assetOwnerId`,
            `assetOwnerName`,
            `assetOwnerUsername`,
            `assetCreatedAt`,
            `assetSharedUntil`,
        ],
    });

    const getList = useCallback(
        (limit, offset, array) => {
            const payload = {
                ...listProperty,
                limit,
                offset,
                filters: [
                    { key: `sharedToUserId`, value: fp.get(`id`, user) },
                    // {key: `projectId`, value: state.activeProjectId}
                ],
            };
            dispatch(postDashboardQueryList(payload))
                .then((res) => {
                    const items = fp.get(`result.rows`, res);
                    if (!fp.isEmpty(items)) {
                        setList(fp.concat(array ? array : list, items));
                        limit > items.length && setIsEmptyList(true);
                    } else {
                        setIsEmptyList(true);
                    }
                })
                .finally(() => setIsLoading(false));
        },
        [list, limit, offset, user, state.activeProjectId],
    );

    const getNextOffset = useCallback(
        (count) => {
            const nextOffset = count * limit;
            setOffset(nextOffset);
            setCount(count);
        },
        [limit],
    );

    const handleReset = useCallback(() => {
        setIsLoading(true);
        setIsEmptyList(false);
        setCount(0);
        setOffset(0);
        setList([]);
        getList(limit, 0, []);
    }, [getList]);

    const handleDebounceTrigger = useCallback(fp.debounce(300, getList), [getList]);

    const handleDebounceNextOffset = useCallback(fp.debounce(300, getNextOffset), [getNextOffset]);

    useEffect(() => {
        !fp.isEmpty(user) && getList(limit, offset, []);
    }, [state.activeProjectId, user]);

    useEffect(
        () => () => {
            //clear
            setList([]);
            setIsEmptyList(false);
            setOffset(0);
            setCount(0);
        },
        [],
    );

    const getApproverInfo = () => {
        console.log();
    };

    return (
        <WidgetWrap id={name} onMove={onMove}>
            <WidgetHead
                title={title}
                icon={icon}
                color={color}
                name={name}
                removable={removable}
                onDelete={onDelete}
                onMoreView={() => history.push(`${menuService.paths.assetSharedBox(spaceId, projectId)}`)}
            />
            <div className={"body"}>
                <div className={"inner"} id={`shared-me-scrollable`}>
                    {(isLoading && (
                        <div className={"loading-block"}>
                            <span className={"loading-content"} />
                        </div>
                    )) || (
                        <>
                            <div className="control">
                                <div className="control-status">
                                    <span>{t(`common::label::Total`, "Total")}: </span>
                                    <strong className="text-primary">{list.length}</strong>
                                    {!fp.isEmpty(list) && (
                                        <strong>of {fp.get(`sharedAssetsToMe.result.totalCount`, state)}</strong>
                                    )}
                                </div>
                                <div className="control-tools">
                                    <button className="btn btn-icon-solid" onClick={handleReset}>
                                        <IconRefresh size={16} />
                                    </button>
                                </div>
                            </div>
                            {(fp.isEmpty(list) && (
                                <div className={"empty"}>
                                    <i className={"sprite sprite-nodata"} />
                                    <p>{t(`common::msg::There is no data`)}</p>
                                </div>
                            )) || (
                                <div className="table table-responsive table-responsive-x">
                                    <InfiniteScroll
                                        scrollableTarget={`shared-me-scrollable`}
                                        dataLength={offset}
                                        next={() => {
                                            if (
                                                fp.isEqual(
                                                    fp.get(`sharedAssetsToMe.result.totalCount`, state),
                                                    list.length,
                                                )
                                            ) {
                                                return false;
                                            } else if (
                                                !fp.isEmpty(fp.get(`sharedAssetsToMe.result.rows`, state)) &&
                                                !fp.isEmpty(list)
                                            ) {
                                                handleDebounceNextOffset(count + 1);
                                                handleDebounceTrigger(limit, (count + 1) * limit);
                                            }
                                        }}
                                        scrollThreshold={`100px`}
                                        hasMore={
                                            !isEmptyList &&
                                            !fp.isEqual(
                                                fp.get(`sharedAssetsToMe.result.totalCount`, state),
                                                list.length,
                                            )
                                        }
                                        loader={
                                            <div className={"loading-block"} style={{ minHeight: 100 }}>
                                                <span className={"loading-content"} />
                                            </div>
                                        }
                                        style={{ overflow: `hidden` }}
                                    >
                                        <table className="table table-hover">
                                            <colgroup>
                                                <col width="200px" />
                                                <col width="100px" />
                                                <col width="120px" />
                                                <col width="150px" />
                                                {/* <col width="100px" /> */}
                                            </colgroup>
                                            <tbody>
                                                {fp.map.convert({ cap: false })(
                                                    (item, index) => (
                                                        <tr key={index}>
                                                            <td className="fit-content">
                                                                <div className="list-content">
                                                                    <div
                                                                        className={`list-thumbnail type-${fp.pipe(
                                                                            fp.get(`assetMediaType`),
                                                                            fp.toLower,
                                                                        )(item)}`}
                                                                    >
                                                                        <MediaImage
                                                                            mediaType={fp.get(`assetMediaType`, item)}
                                                                            url={fp.get(`assetThumbnailUrl`, item)}
                                                                        />
                                                                    </div>

                                                                    <div className={"list-title"}>
                                                                        <Link
                                                                            to={`${menuService.paths.assetsAssetDetail(
                                                                                spaceId,
                                                                                projectId,
                                                                                item.assetId,
                                                                            )}?projectId=${fp.get(`projectId`, item)}`}
                                                                        >
                                                                            <span className={"text-ellipsis"}>
                                                                                {fp.get(`assetName`, item)}
                                                                            </span>
                                                                        </Link>
                                                                        <small>{fp.get(`assetId`, item)}</small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td data-title={t(`common::label::${"Projects"}`)}>
                                                                <span className="text-wrap">
                                                                    {fp.get(`projectName`, item)}
                                                                </span>
                                                            </td>
                                                            <td data-title={t(`common::label::${"Type"}`)}>
                                                                <div className="d-flex">
                                                                    <i className={`sprite`}>
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "VIDEO" && (
                                                                            <IconVideo
                                                                                htmlColor={`#9fa3b2`}
                                                                                size={16}
                                                                            />
                                                                        )}
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "AUDIO" && (
                                                                            <IconAudio
                                                                                htmlColor={`#9fa3b2`}
                                                                                size={16}
                                                                            />
                                                                        )}
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "FILE" && (
                                                                            <IconFile htmlColor={`#9fa3b2`} size={16} />
                                                                        )}
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "IMAGE" && (
                                                                            <IconImage
                                                                                htmlColor={`#9fa3b2`}
                                                                                size={16}
                                                                            />
                                                                        )}
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "TEXT" && (
                                                                            <IconCaption
                                                                                htmlColor={`#9fa3b2`}
                                                                                size={16}
                                                                            />
                                                                        )}
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "CAPTION" && (
                                                                            <IconCaption
                                                                                htmlColor={`#9fa3b2`}
                                                                                size={16}
                                                                            />
                                                                        )}
                                                                        {fp.getOr("none", `assetMediaType`, item) ===
                                                                            "ZIP" && (
                                                                            <IconZip htmlColor={`#9fa3b2`} size={16} />
                                                                        )}
                                                                    </i>

                                                                    <div className="d-flex flex-column">
                                                                        <span>{fp.get(`assetMediaType`, item)}</span>
                                                                        <span>{fp.get(`assetType`, item)}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            {/* 버저닝 처리  */}
                                                            {compareVersions(
                                                                state.version,
                                                                VERSION_CM_9337_RECENT_ASSET_SHARED_ME,
                                                            ) < 0 ? (
                                                                <td data-title={t(`common::label::${"Owner"}`)}>
                                                                    <div className="list-content">
                                                                        <div className="list-title">
                                                                            <p>
                                                                                <span className="text-ellipsis">
                                                                                    {fp.get(`assetOwnerName`, item)}
                                                                                </span>
                                                                            </p>
                                                                            <small>
                                                                                {fp.get(`assetOwnerUsername`, item)}
                                                                            </small>
                                                                            <TooltipUserInfo
                                                                                target={item}
                                                                                id={fp.get(`assetOwnerId`, item)}
                                                                            />
                                                                            <p>
                                                                                <time>
                                                                                    {convertUtcToLocal(
                                                                                        fp.get(
                                                                                            `assetSharedUntil`,
                                                                                            item,
                                                                                        ),
                                                                                    )}
                                                                                </time>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            ) : (
                                                                <td data-title={t(`common::label::${"Approver"}`)}>
                                                                    <div className="list-content">
                                                                        <span
                                                                            data-tooltip={t(`common::label::Shared by`)}
                                                                            style={{ height: "16px" }}
                                                                        >
                                                                            <IconShareRequest
                                                                                size={16}
                                                                                htmlColor={`#6f6f83`}
                                                                            />
                                                                        </span>

                                                                        <div
                                                                            className="list-title"
                                                                            style={{ marginLeft: "8px" }}
                                                                        >
                                                                            <p>
                                                                                <span className="text-ellipsis">
                                                                                    {fp.get(`grantedByName`, item)}
                                                                                </span>
                                                                            </p>
                                                                            <small>
                                                                                {fp.get(`grantedByUsername`, item)}
                                                                            </small>
                                                                            <TooltipUserInfo
                                                                                target={item}
                                                                                id={fp.get(`grantedById`, item)}
                                                                            />
                                                                            <p>
                                                                                <time>
                                                                                    {convertUtcToLocal(
                                                                                        fp.get(
                                                                                            `assetSharedUntil`,
                                                                                            item,
                                                                                        ),
                                                                                    )}
                                                                                </time>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ),
                                                    list,
                                                )}
                                            </tbody>
                                        </table>
                                    </InfiniteScroll>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </WidgetWrap>
    );
};

RecentAssetSharedMe.propTypes = {
    user: PropTypes.object,
    name: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.node,
    onMove: PropTypes.func,
    onDelete: PropTypes.func,
    removable: PropTypes.bool,
};

export default RecentAssetSharedMe;
